import React, { useState, useEffect, useCallback } from 'react';
import { Container, Typography, Grid, Paper, CircularProgress, Button, Snackbar, Alert, Box } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Bar, BarChart } from 'recharts';
import api from '../utils/api';

const Statistics = () => {
    const [stats, setStats] = useState(null);
    const [orderTimeSeries, setOrderTimeSeries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
          const [statsResponse, timeSeriesResponse] = await Promise.all([
            api.get('/statistics/latest'),
            api.get('/statistics/order-time-series')
          ]);
          setStats(statsResponse.data);
          setOrderTimeSeries(timeSeriesResponse.data);
        } catch (error) {
          console.error('Error fetching statistics:', error);
          setError('Failed to fetch statistics. Please try again.');
        } finally {
          setLoading(false);
        }
    }, []);
    
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleUpdateStatistics = async () => {
        setUpdating(true);
        setError(null);
        try {
            await api.post('/statistics/update');
            await fetchData(); // Refresh all data after update
            setSnackbar({ open: true, message: 'Statistics updated successfully', severity: 'success' });
        } catch (error) {
            console.error('Error updating statistics:', error);
            setError('Failed to update statistics. Please try again.');
            setSnackbar({ open: true, message: 'Failed to update statistics', severity: 'error' });
        } finally {
            setUpdating(false);
        }
    };

    if (loading) {
        return (
            <Container>
                <CircularProgress />
                <Typography>Loading statistics...</Typography>
            </Container>
        );
    }

    if (error) {
        return (
            <Container>
                <Typography color="error">{error}</Typography>
                <Button onClick={fetchData} variant="contained" color="primary" style={{ marginTop: '20px' }}>
                Retry
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}
                >
                    Business Statistics
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    View comprehensive analytics and insights about your inventory business
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpdateStatistics}
                    disabled={updating}
                    sx={{
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                            boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                        }
                    }}
                >
                    {updating ? 'Updating...' : 'Update Statistics'}
                </Button>
            </Box>

            {stats && (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white'
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, opacity: 0.9 }}>Most Ordered Product</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                {stats.mostOrderedProduct?.productName}
                            </Typography>
                            <Typography sx={{ opacity: 0.8 }}>
                                {stats.mostOrderedProduct?.orderCount} orders
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                color: 'white'
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, opacity: 0.9 }}>Least Ordered Product</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                {stats.leastOrderedProduct?.productName}
                            </Typography>
                            <Typography sx={{ opacity: 0.8 }}>
                                {stats.leastOrderedProduct?.orderCount} orders
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                                color: 'white'
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, opacity: 0.9 }}>Total Orders</Typography>
                            <Typography variant="h3" sx={{ fontWeight: 700 }}>{stats.numberOfOrders}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                                color: 'white'
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, opacity: 0.9 }}>Total Products</Typography>
                            <Typography variant="h3" sx={{ fontWeight: 700 }}>{stats.numberOfProducts}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
                                color: 'white'
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, opacity: 0.9 }}>Total Parties</Typography>
                            <Typography variant="h3" sx={{ fontWeight: 700 }}>{stats.numberOfParties}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                                color: 'white'
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, opacity: 0.9 }}>Top Customer</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                {stats.partyWithMostOrders?.partyName}
                            </Typography>
                            <Typography sx={{ opacity: 0.8 }}>
                                {stats.partyWithMostOrders?.orderCount} orders
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                                color: '#333'
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, opacity: 0.8 }}>Least Active Customer</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                {stats.partyWithLeastOrders?.partyName}
                            </Typography>
                            <Typography sx={{ opacity: 0.7 }}>
                                {stats.partyWithLeastOrders?.orderCount} orders
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                                color: '#333'
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, opacity: 0.8 }}>Average Order Value</Typography>
                            <Typography variant="h3" sx={{ fontWeight: 700 }}>₹{stats.averageOrderValue.toFixed(2)}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                background: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
                                color: '#333'
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, opacity: 0.8 }}>Total Revenue</Typography>
                            <Typography variant="h3" sx={{ fontWeight: 700 }}>₹{stats.totalRevenue.toFixed(2)}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Product Order Counts</Typography>
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart
                                    data={stats.productOrderCounts}
                                    layout="vertical"
                                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" />
                                    <YAxis dataKey="productName" type="category" width={150} />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="orderCount" fill="#8884d8" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {orderTimeSeries.length > 0 && (
                <Paper
                    elevation={3}
                    sx={{
                        p: 3,
                        mt: 4,
                        borderRadius: 3,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Order Time Series</Typography>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={orderTimeSeries}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="_id" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="count" stroke="#8884d8" />
                        </LineChart>
                    </ResponsiveContainer>
                </Paper>
            )}

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
  );
};

export default Statistics;